import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Temporal } from 'temporal-polyfill';
import type { ClassSeries, Location, Weekday } from '../domain/types';
import { formatSeriesSummary } from '../domain/format';
import { formatDayStamp } from '../domain/calendar';
import { expandDates, expandPastDates } from '../domain/recurrence';
import { isClaimBased } from '../domain/schedule';
import { makeOccurrenceKey } from '../domain/occurrenceKey';
import { errorFor, isValidIsoDate, validateSeries } from '../domain/validation';
import type { SeriesDraft, ValidationResult } from '../domain/validation';
import { useAsync, useRepository } from './repositoryContext';
import { ErrorBanner, Field } from './components';
import { RecurrenceEditor } from './RecurrenceEditor';
import { CLASS_COLORS, colorName, nextClassColor } from '../domain/palette';
import { LocationField, browserTimeZone, type LocationChoice } from './LocationField';

/** Common class lengths, so duration is a tap rather than a typed number. */
const DURATIONS = [30, 45, 60, 75, 90];

function today(): string {
  return Temporal.Now.plainDateISO().toString();
}

function weekdayOf(isoDate: string): Weekday {
  return Temporal.PlainDate.from(isoDate).dayOfWeek as Weekday;
}

function emptyDraft(colorIndex: number): SeriesDraft {
  const start = today();
  return {
    title: '',
    locationId: '',
    color: nextClassColor(colorIndex),
    startTime: '18:00',
    durationMinutes: 60,
    // Default to repeating on whatever weekday today is — the most likely intent
    // when someone adds the class they teach.
    recurrence: { freq: 'weekly', byWeekday: [weekdayOf(start)] },
    startDate: start,
    endDate: null,
    notes: '',
    isSub: false,
    shared: false,
  };
}

function toDraft(series: ClassSeries): SeriesDraft {
  return {
    title: series.title,
    locationId: series.locationId,
    color: series.color,
    startTime: series.startTime,
    durationMinutes: series.durationMinutes,
    recurrence: series.recurrence,
    startDate: series.startDate,
    endDate: series.endDate,
    notes: series.notes,
    isSub: series.isSub,
    shared: series.shared,
  };
}

export function ClassForm(props: {
  locations: readonly Location[];
  /** Omitted when creating. */
  series?: ClassSeries | undefined;
  /** How many classes already exist, so a new one starts on an unused colour. */
  seriesCount?: number;
  onSaved: () => void;
  onCancel: () => void;
  /** Editing only: archiving and deleting live here rather than cluttering the list. */
  onArchiveToggle?: (() => Promise<void>) | undefined;
  onDelete?: (() => Promise<void>) | undefined;
}): JSX.Element {
  const repo = useRepository();
  const editing = props.series !== undefined;

  const [draft, setDraft] = useState<SeriesDraft>(() =>
    props.series === undefined ? emptyDraft(props.seriesCount ?? 0) : toDraft(props.series),
  );
  const [place, setPlace] = useState<LocationChoice>(() => {
    if (props.series !== undefined) {
      return { kind: 'existing', id: props.series.locationId };
    }
    const first = props.locations[0];
    return first === undefined ? { kind: 'new', name: '' } : { kind: 'existing', id: first.id };
  });
  const [validation, setValidation] = useState<ValidationResult>({ ok: true });
  const [placeError, setPlaceError] = useState<string | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [customDuration, setCustomDuration] = useState(
    () => !DURATIONS.includes(draft.durationMinutes),
  );

  /**
   * Turn ticks the user changed in this form session, date → mine. Part of the
   * draft like every other field: persisted on Save (which is also when a new
   * class first has an id for its claims to reference), discarded on Cancel.
   * Only explicit changes are recorded, so ticks already stored for an existing
   * class are simply left alone.
   */
  const [turnChanges, setTurnChanges] = useState<ReadonlyMap<string, boolean>>(new Map());
  const claims = useAsync(() => repo.listClaims(), []);

  function patch(changes: Partial<SeriesDraft>): void {
    setDraft((current) => ({ ...current, ...changes }));
  }

  function toggleTurn(date: string, mine: boolean): void {
    setTurnChanges((current) => new Map(current).set(date, mine));
  }

  /**
   * Ticks every class that has already happened, called when a previously
   * fully-owned class becomes claim-based (switched to shared, or a repeating
   * class switched to sub). Those classes were taught by this instructor —
   * nobody else's turns existed before — so claiming them keeps taught history
   * on the schedule and in stats instead of silently un-owning it. They are
   * ordinary draft ticks: visible in the turns box, individually untickable,
   * persisted on Save.
   */
  function claimTaughtTurns(): void {
    const series = seriesFromDraft(draft);
    if (series === null || draftTimeZone === undefined || series.recurrence.freq === 'once') {
      return;
    }
    const taught = expandPastDates(series, draftTimeZone, Temporal.Now.plainDateISO());

    setTurnChanges((current) => {
      const next = new Map(current);
      for (const date of taught) {
        // Explicit unticks from earlier in this session are respected.
        if (!next.has(date.toString())) {
          next.set(date.toString(), true);
        }
      }
      return next;
    });
  }

  /**
   * Whether the STORED class already owns its occurrences through claims;
   * flipping a switch that keeps it claim-based must not re-claim turns the
   * instructor previously handed back.
   */
  const storedClaimBased = props.series !== undefined && isClaimBased(props.series);

  const placeName =
    place.kind === 'existing'
      ? props.locations.find((location) => location.id === place.id)?.name
      : place.name;

  // The turns list needs a zone before the class is saved — or even before the
  // place exists. A typed-but-uncreated place gets the phone's zone, exactly
  // what resolveLocationId will stamp on it at save time.
  const draftTimeZone =
    place.kind === 'existing'
      ? props.locations.find((location) => location.id === place.id)?.timeZone
      : browserTimeZone();

  // What is already claimed in storage (editing only); the session's unsaved
  // tick changes are layered on top of this in the list.
  const existingClaimDates: ReadonlySet<string> = new Set(
    props.series === undefined
      ? []
      : (claims.data ?? [])
          .filter((claim) => claim.seriesId === props.series?.id)
          .map((claim) => claim.originalDate),
  );

  async function resolveLocationId(): Promise<string> {
    if (place.kind === 'existing') {
      return place.id;
    }
    // A typed place is created on save, which is what removes the old
    // "add a location first" dead end.
    const created = await repo.createLocation({
      name: place.name.trim(),
      address: '',
      timeZone: browserTimeZone(),
    });
    return created.id;
  }

  async function save(event: Event): Promise<void> {
    event.preventDefault();

    if (place.kind === 'new' && place.name.trim().length === 0) {
      setPlaceError('Where is this class?');
      return;
    }
    setPlaceError(undefined);

    // Validate everything except the location, which may not exist yet.
    const candidate: SeriesDraft = {
      ...draft,
      title: draft.title.trim(),
      notes: draft.notes.trim(),
      locationId: place.kind === 'existing' ? place.id : 'pending',
    };
    const result = validateSeries(candidate);
    setValidation(result);
    if (!result.ok) {
      return;
    }

    try {
      const locationId = await resolveLocationId();
      const payload: SeriesDraft = { ...candidate, locationId };
      let seriesId: string;
      if (props.series === undefined) {
        seriesId = (await repo.createSeries({ ...payload, active: true, archivedAt: null })).id;
      } else {
        seriesId = (await repo.updateSeries(props.series.id, payload)).id;
      }

      // Persist the turn ticks alongside the class they belong to. Skipped
      // when the class ended up fully owned (neither shared nor a repeating
      // sub): it owns every occurrence, and claims would be meaningless noise.
      if (isClaimBased(payload)) {
        for (const [date, mine] of turnChanges) {
          if (mine) {
            await repo.putClaim({
              seriesId,
              occurrenceKey: makeOccurrenceKey(seriesId, date),
              originalDate: date,
            });
          } else {
            await repo.clearClaim(makeOccurrenceKey(seriesId, date));
          }
        }
      }

      setError(undefined);
      props.onSaved();
    } catch (cause: unknown) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  return (
    <form class="screen" onSubmit={(event) => void save(event)}>
      <header class="screen-bar">
        <button type="button" class="bar-action" onClick={props.onCancel}>
          Cancel
        </button>
        <h2>{editing ? 'Edit class' : 'New class'}</h2>
        <button type="submit" class="bar-action strong">
          Save
        </button>
      </header>

      <div class="screen-body">
        <ErrorBanner error={error} />

        <Field label="What" error={errorFor(validation, 'title')}>
          <input
            type="text"
            placeholder="Vinyasa Flow"
            value={draft.title}
            onInput={(event) => patch({ title: (event.target as HTMLInputElement).value })}
          />
        </Field>

        <Field label="Description (optional)">
          <textarea
            rows={2}
            value={draft.notes}
            onInput={(event) => patch({ notes: (event.target as HTMLTextAreaElement).value })}
          />
        </Field>

        <Field label="Colour" error={errorFor(validation, 'color')}>
          <div class="swatches" role="group" aria-label="Colour">
            {CLASS_COLORS.map((color) => (
              <button
                key={color}
                type="button"
                class={`swatch${draft.color.toLowerCase() === color ? ' selected' : ''}`}
                style={{ background: color }}
                aria-label={colorName(color)}
                aria-pressed={draft.color.toLowerCase() === color}
                onClick={() => patch({ color })}
              />
            ))}
          </div>
        </Field>

        <LocationField
          locations={props.locations}
          value={place}
          error={placeError}
          onChange={setPlace}
        />

        {/* One mutually-exclusive choice instead of two switches: a class is
            the instructor's own, a rotation they belong to, or someone else's
            that they're covering. Shared and sub both mean "pick your dates"
            (the box by the schedule fields); a one-time class can only be
            mine or a sub, since a single date has no rotation to share. */}
        <div class="field">
          <span class="field-label" id="ownership-label">
            This class is
          </span>
          <div class="segmented" role="group" aria-labelledby="ownership-label">
            <button
              type="button"
              class={`segment${!draft.shared && !draft.isSub ? ' selected' : ''}`}
              aria-pressed={!draft.shared && !draft.isSub}
              onClick={() => patch({ shared: false, isSub: false })}
            >
              Mine
            </button>
            {draft.recurrence.freq !== 'once' ? (
              <button
                type="button"
                class={`segment${draft.shared ? ' selected' : ''}`}
                aria-pressed={draft.shared}
                onClick={() => {
                  // Becoming claim-based claims taught history; a class already
                  // claim-based in storage keeps its stored claims instead.
                  if (!draft.shared && !storedClaimBased) {
                    claimTaughtTurns();
                  }
                  patch({ shared: true, isSub: false });
                }}
              >
                Shared
              </button>
            ) : null}
            <button
              type="button"
              class={`segment${draft.isSub ? ' selected' : ''}`}
              aria-pressed={draft.isSub}
              onClick={() => {
                if (!draft.isSub && draft.recurrence.freq !== 'once' && !storedClaimBased) {
                  claimTaughtTurns();
                }
                patch({ isSub: true, shared: false });
              }}
            >
              Sub
            </button>
          </div>
        </div>

        <div class="inline-row">
          <Field label="Starts" error={errorFor(validation, 'startTime')}>
            <input
              type="time"
              value={draft.startTime}
              onInput={(event) => patch({ startTime: (event.target as HTMLInputElement).value })}
            />
          </Field>

          <Field label="Lasts" error={errorFor(validation, 'durationMinutes')}>
            {customDuration ? (
              <input
                type="number"
                min={5}
                max={480}
                step={5}
                value={draft.durationMinutes}
                onInput={(event) =>
                  patch({ durationMinutes: Number((event.target as HTMLInputElement).value) })
                }
              />
            ) : (
              <select
                value={String(draft.durationMinutes)}
                onChange={(event) => {
                  const raw = (event.target as HTMLSelectElement).value;
                  if (raw === 'custom') {
                    setCustomDuration(true);
                    return;
                  }
                  patch({ durationMinutes: Number(raw) });
                }}
              >
                {DURATIONS.map((minutes) => (
                  <option key={minutes} value={String(minutes)}>
                    {minutes === 60 ? '1 hour' : `${minutes} min`}
                  </option>
                ))}
                <option value="custom">Custom…</option>
              </select>
            )}
          </Field>
        </div>

        <RecurrenceEditor
          value={draft.recurrence}
          error={errorFor(validation, 'recurrence')}
          defaultWeekday={weekdayOf(draft.startDate)}
          onChange={(recurrence) =>
            // A one-time class has no "last class"; a lingering end date from a
            // previously chosen repeating rule could silently hide it. Sharing
            // likewise only makes sense for a repeating slot.
            patch(
              recurrence.freq === 'once'
                ? { recurrence, endDate: null, shared: false }
                : { recurrence },
            )
          }
        />

        {/* Each rule shows only the dates it actually needs: a one-time class is
            a single date; a repeating one has a first class and an optional last. */}
        {draft.recurrence.freq === 'once' ? (
          <Field label="Date" error={errorFor(validation, 'startDate')}>
            <input
              type="date"
              value={draft.startDate}
              onInput={(event) => patch({ startDate: (event.target as HTMLInputElement).value })}
            />
          </Field>
        ) : (
          <div class="inline-row">
            <Field label="First class" error={errorFor(validation, 'startDate')}>
              <input
                type="date"
                value={draft.startDate}
                onInput={(event) => patch({ startDate: (event.target as HTMLInputElement).value })}
              />
            </Field>

            <Field
              label="Last class"
              error={errorFor(validation, 'endDate')}
              hint="Optional — leave blank to keep going."
            >
              {/* Some native pickers (notably iOS) offer no way to empty a
                  date field, so a small × appears whenever a date is set.
                  Only the LAST date can be blanked: a class must always know
                  when it starts. */}
              <span class="date-clear">
                <input
                  type="date"
                  value={draft.endDate ?? ''}
                  onInput={(event) => {
                    const raw = (event.target as HTMLInputElement).value;
                    patch({ endDate: raw === '' ? null : raw });
                  }}
                />
                {draft.endDate !== null ? (
                  <button
                    type="button"
                    class="icon-button"
                    aria-label="Clear the last date"
                    onClick={() => patch({ endDate: null })}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            </Field>
          </div>
        )}

        {/* The date picker for a claim-based class (shared rotation or a
            repeating sub) lives by the schedule fields it depends on. */}
        {isClaimBased(draft) ? (
          <TurnsSection
            draft={draft}
            timeZone={draftTimeZone}
            existingClaimDates={existingClaimDates}
            disabled={props.series !== undefined && claims.loading}
            changes={turnChanges}
            onToggle={toggleTurn}
            loadError={props.series === undefined ? undefined : claims.error}
            // Wording only: a rotation has "turns", a sub engagement has
            // "days you're covering". The machinery is identical.
            variant={draft.shared ? 'shared' : 'sub'}
          />
        ) : null}

        <p class="summary" aria-live="polite">
          {formatSeriesSummary({
            recurrence: draft.recurrence,
            startTime: draft.startTime,
            startDate: draft.startDate,
            locationName: placeName,
          })}
        </p>

        {editing ? (
          <div class="danger-zone">
            {props.onArchiveToggle !== undefined ? (
              <button type="button" class="block" onClick={() => void props.onArchiveToggle?.()}>
                {props.series?.active === false ? 'Restore this class' : 'Archive this class'}
              </button>
            ) : null}
            {props.onDelete !== undefined ? (
              <button type="button" class="block danger" onClick={() => void props.onDelete?.()}>
                Delete this class
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </form>
  );
}

/** How many more turns each scroll-to-bottom loads. */
const TURNS_PAGE = 20;
/** Practical horizon for an open-ended rotation, mirroring the ICS export's. */
const OPEN_ENDED_TURN_YEARS = 5;
/** Chunk size for incremental expansion; monthly rules yield ~3 dates per chunk. */
const EXPANSION_CHUNK_DAYS = 91;

/**
 * A synthetic live series carrying the draft's rule and dates, so expansion
 * reflects exactly what the user is looking at rather than what was last
 * saved. Null while the draft's dates are invalid (half-typed, or reversed) —
 * expansion would throw on them.
 */
function seriesFromDraft(draft: SeriesDraft): ClassSeries | null {
  const datesUsable =
    isValidIsoDate(draft.startDate) &&
    (draft.endDate === null || (isValidIsoDate(draft.endDate) && draft.endDate >= draft.startDate));
  if (!datesUsable) {
    return null;
  }
  return {
    id: 'draft',
    createdAt: '',
    updatedAt: '',
    deletedAt: null,
    title: draft.title,
    locationId: draft.locationId,
    color: draft.color,
    startTime: draft.startTime,
    durationMinutes: draft.durationMinutes,
    recurrence: draft.recurrence,
    startDate: draft.startDate,
    endDate: draft.endDate,
    notes: draft.notes,
    isSub: draft.isSub,
    shared: true,
    active: true,
    archivedAt: null,
  };
}

/**
 * The next `count` dates the rule produces from `from`, expanding lazily in
 * chunks rather than materialising years of a daily class up front.
 *
 * `from` is the series' own start date in the turns box, so PAST dates are
 * offered too — history is claimable, not just the future. An open-ended
 * series stops at `openEndedHorizon` (today + 5 years in practice) rather
 * than the old from-relative horizon, so a class that started years ago still
 * reaches years ahead.
 *
 * `exhausted` reports that the series has no dates beyond the returned ones —
 * either its end date was reached or the horizon was — so the infinite scroll
 * knows when to stop asking.
 */
export function turnDatesPage(
  series: ClassSeries,
  timeZone: string,
  from: Temporal.PlainDate,
  openEndedHorizon: Temporal.PlainDate,
  count: number,
): { dates: Temporal.PlainDate[]; exhausted: boolean } {
  const hardEnd =
    series.endDate === null ? openEndedHorizon : Temporal.PlainDate.from(series.endDate);

  const dates: Temporal.PlainDate[] = [];
  let cursor = from;
  while (dates.length <= count && Temporal.PlainDate.compare(cursor, hardEnd) <= 0) {
    const chunkEnd = cursor.add({ days: EXPANSION_CHUNK_DAYS - 1 });
    const windowEnd = Temporal.PlainDate.compare(chunkEnd, hardEnd) > 0 ? hardEnd : chunkEnd;
    dates.push(...expandDates({ series, timeZone, rangeStart: cursor, rangeEnd: windowEnd }));
    cursor = windowEnd.add({ days: 1 });
  }

  // Having found even one date past the requested count proves there are more.
  return dates.length > count
    ? { dates: dates.slice(0, count), exhausted: false }
    : { dates, exhausted: true };
}

/**
 * The upcoming turns of a shared rotation, each tickable as "mine".
 *
 * Driven entirely by the in-progress DRAFT: flip the shared switch and the
 * dates appear immediately, change the rule or the first/last date and the
 * list follows — no save needed to look. The tick changes themselves are part
 * of the form (`changes`), persisted by Save once the class has an id, and
 * discarded by Cancel like any other unsaved edit.
 *
 * This is the ONLY place turns are claimed or handed back — the schedule
 * strictly shows the instructor's own classes, so it has nothing to claim from.
 */
function TurnsSection(props: {
  draft: SeriesDraft;
  /** Undefined when the chosen place no longer exists. */
  timeZone: string | undefined;
  /** Dates already claimed in storage (empty for a new class). */
  existingClaimDates: ReadonlySet<string>;
  /** This session's unsaved tick changes, layered over the stored claims. */
  changes: ReadonlyMap<string, boolean>;
  disabled: boolean;
  loadError: Error | undefined;
  onToggle: (date: string, mine: boolean) => void;
  /** Wording only: a rotation has turns; a sub engagement has covered days. */
  variant: 'shared' | 'sub';
}): JSX.Element | null {
  const [count, setCount] = useState(TURNS_PAGE);
  // Frozen on mount so scrolling across midnight cannot reshuffle the list.
  const [today] = useState(() => Temporal.Now.plainDateISO());

  if (props.timeZone === undefined) {
    // Place removed: expansion is impossible, and the schedule already says so.
    return null;
  }

  const draftSeries = seriesFromDraft(props.draft);

  // The list runs from the class's own first date — past dates included, so
  // history stays claimable — through the end date or the open-ended horizon.
  const { dates, exhausted } =
    draftSeries === null
      ? { dates: [], exhausted: true }
      : turnDatesPage(
          draftSeries,
          props.timeZone,
          Temporal.PlainDate.from(draftSeries.startDate),
          today.add({ years: OPEN_ENDED_TURN_YEARS }),
          count,
        );

  function maybeLoadMore(event: Event): void {
    const box = event.currentTarget as HTMLDivElement;
    // Top up one page whenever the bottom comes within a row or two of view.
    if (!exhausted && box.scrollTop + box.clientHeight >= box.scrollHeight - 80) {
      setCount((current) => current + TURNS_PAGE);
    }
  }

  return (
    <fieldset class="turns">
      <legend>{props.variant === 'sub' ? "Days you're subbing" : 'Your turns'}</legend>
      <p class="muted form-hint">
        {props.variant === 'sub'
          ? "Tick the days you're covering — days you've already subbed start ticked. "
          : "Tick the classes you're teaching — classes you've already taught start ticked. "}
        Only ticked {props.variant === 'sub' ? 'days' : 'classes'} appear on your schedule, in
        stats, and in calendar exports.
      </p>
      <ErrorBanner error={props.loadError} />
      {draftSeries === null ? (
        <p class="muted">Pick valid first and last dates to see the classes.</p>
      ) : dates.length === 0 ? (
        <p class="muted">No classes between these dates.</p>
      ) : (
        <div class="turn-scroll" onScroll={maybeLoadMore}>
          <ul class="turn-list">
            {dates.map((date) => {
              const iso = date.toString();
              const mine = props.changes.get(iso) ?? props.existingClaimDates.has(iso);
              return (
                <li key={iso}>
                  <label class="turn-row">
                    <span>{formatDayStamp(date)}</span>
                    <input
                      type="checkbox"
                      checked={mine}
                      disabled={props.disabled}
                      onChange={(event) =>
                        props.onToggle(iso, (event.target as HTMLInputElement).checked)
                      }
                    />
                  </label>
                </li>
              );
            })}
          </ul>
          {exhausted ? null : (
            <p class="muted turn-more" aria-hidden="true">
              Scroll for more…
            </p>
          )}
        </div>
      )}
    </fieldset>
  );
}
